import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { Truck, Building2, ArrowRight, Check, CalendarCheck, Bell, Users } from "lucide-react";

const BUSINESS_TYPES = [
  { id: "transport", label: "Transport / Trucking" },
  { id: "logistics", label: "Logistics / Last-mile" },
  { id: "cab_aggregator", label: "Cab / Ride-hailing fleet" },
  { id: "school_bus", label: "School / Staff bus operator" },
  { id: "corporate_fleet", label: "Corporate fleet" },
  { id: "rental", label: "Car rental / Self-drive" },
  { id: "other", label: "Other" },
];

const BENEFITS = [
  { icon: CalendarCheck, title: "Fleet-wide expiry calendar", body: "See when every vehicle's PUC expires. Bulk-book renewals in one click." },
  { icon: Bell, title: "Automated 3-day reminders", body: "Email + SMS to your ops team before each vehicle's certificate expires." },
  { icon: Users, title: "Priority partner slots", body: "Verified commercial accounts get preferred booking windows during peak hours." },
];

export default function CommercialRegister() {
  const { user, register, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(user ? 2 : 1);
  const [submitting, setSubmitting] = useState(false);

  // Step 1 — user account (if not logged in)
  const [account, setAccount] = useState({ name: "", email: "", phone: "", password: "" });
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Step 2 — company profile
  const [company, setCompany] = useState({
    company_name: "",
    business_type: "",
    gst_number: "",
    company_address: "",
    city: "",
    state: "",
    pincode: "",
    contact_person: user?.name || "",
    contact_phone: "",
    contact_email: user?.email || "",
    fleet_size: 5,
  });

  useEffect(() => {
    if (user) {
      setStep(2);
      setCompany((c) => ({ ...c, contact_person: c.contact_person || user.name, contact_email: c.contact_email || user.email }));
    }
  }, [user]);

  const setField = (obj, setter) => (k) => (e) => setter({ ...obj, [k]: e.target.value });

  const nextFromStep1 = async () => {
    if (!account.name || !account.email || !account.password || account.password.length < 6) {
      toast.error("Fill all fields (password min 6 chars)");
      return;
    }
    if (!acceptedTerms) { toast.error("Please accept the Terms & Privacy Policy"); return; }
    setSubmitting(true);
    try {
      await register({ ...account, role: "customer" });
      setCompany((c) => ({ ...c, contact_person: c.contact_person || account.name, contact_email: c.contact_email || account.email, contact_phone: c.contact_phone || account.phone }));
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  const submitCompany = async (e) => {
    e.preventDefault();
    if (!company.business_type) { toast.error("Select business type"); return; }
    setSubmitting(true);
    try {
      await api.post("/commercial/register", { ...company, fleet_size: Number(company.fleet_size) || 1 });
      await refresh();
      toast.success("Commercial account activated. Let's add your fleet.");
      navigate("/dashboard/customer");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-6 py-16 md:py-20 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <div className="inline-flex items-center gap-2 border border-primary/30 bg-primary/10 px-3 py-1 mb-6">
              <Truck className="w-4 h-4 text-primary" />
              <span className="text-xs uppercase tracking-[0.2em] font-medium text-primary">For fleets & transporters</span>
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-black tracking-tighter leading-[0.95]">
              One dashboard.<br />Every vehicle&apos;s PUC.<br /><span className="text-primary">Zero missed renewals.</span>
            </h1>
            <p className="mt-6 text-muted-foreground max-w-lg leading-relaxed">Transporters, cab aggregators, school bus operators, corporate fleets — manage pollution compliance across your entire fleet from a single account.</p>

            <div className="mt-10 space-y-4">
              {BENEFITS.map((b) => (
                <div key={b.title} className="flex items-start gap-4">
                  <div className="w-10 h-10 border border-primary/30 bg-primary/10 flex items-center justify-center flex-shrink-0"><b.icon className="w-5 h-5 text-primary" /></div>
                  <div>
                    <div className="font-display font-bold text-lg">{b.title}</div>
                    <div className="text-sm text-muted-foreground">{b.body}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-6">
            <Card className="rounded-lg border-border bg-card p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className={`w-8 h-8 flex items-center justify-center border ${step >= 1 ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground"}`}>{step > 1 ? <Check className="w-4 h-4" /> : "1"}</div>
                <div className="flex-1 h-px bg-border" />
                <div className={`w-8 h-8 flex items-center justify-center border ${step >= 2 ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground"}`}>2</div>
              </div>

              {step === 1 && (
                <div className="space-y-4" data-testid="commercial-step-1">
                  <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Step 1 — Owner account</div>
                  <h2 className="font-display text-2xl font-bold tracking-tight">Create your login</h2>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Owner name</Label>
                    <Input data-testid="comm-owner-name" value={account.name} onChange={setField(account, setAccount)("name")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Email</Label>
                      <Input data-testid="comm-owner-email" type="email" value={account.email} onChange={setField(account, setAccount)("email")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Phone</Label>
                      <Input data-testid="comm-owner-phone" value={account.phone} onChange={setField(account, setAccount)("phone")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Password</Label>
                    <Input data-testid="comm-owner-password" type="password" value={account.password} onChange={setField(account, setAccount)("password")} className="rounded-lg bg-secondary border-border h-11 mt-2" placeholder="Min 6 characters" />
                  </div>
                  <Button data-testid="comm-step1-next" onClick={nextFromStep1} disabled={submitting || !acceptedTerms} className="w-full rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-12">
                    Continue to company details <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <label className="flex items-start gap-3 text-sm text-muted-foreground select-none">
                    <Checkbox
                      data-testid="comm-accept-terms"
                      checked={acceptedTerms}
                      onCheckedChange={(v) => setAcceptedTerms(!!v)}
                      className="mt-0.5 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                    />
                    <span>
                      I accept VayuCheck&apos;s{" "}
                      <Link to="/terms" target="_blank" className="text-primary hover:underline font-medium">Terms &amp; Conditions</Link>
                      {" "}and{" "}
                      <Link to="/privacy" target="_blank" className="text-primary hover:underline font-medium">Privacy Policy</Link>.
                    </span>
                  </label>
                </div>
              )}

              {step === 2 && (
                <form onSubmit={submitCompany} className="space-y-4" data-testid="commercial-step-2">
                  <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2 flex items-center gap-2">
                    <Building2 className="w-4 h-4" /> Step 2 — Company profile
                  </div>
                  <h2 className="font-display text-2xl font-bold tracking-tight">Tell us about your fleet</h2>

                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Company / Business name</Label>
                    <Input data-testid="comm-company-name" required value={company.company_name} onChange={setField(company, setCompany)("company_name")} className="rounded-lg bg-secondary border-border h-11 mt-2" placeholder="e.g. Delhi Logistics Pvt Ltd" />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Business type</Label>
                      <Select value={company.business_type} onValueChange={(v) => setCompany({ ...company, business_type: v })}>
                        <SelectTrigger data-testid="comm-business-type" className="rounded-lg bg-secondary border-border h-11 mt-2"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent className="rounded-lg">
                          {BUSINESS_TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Approx. fleet size</Label>
                      <Input data-testid="comm-fleet-size" type="number" min="1" required value={company.fleet_size} onChange={setField(company, setCompany)("fleet_size")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">GST number (optional)</Label>
                    <Input data-testid="comm-gst" value={company.gst_number} onChange={setField(company, setCompany)("gst_number")} className="rounded-lg bg-secondary border-border h-11 mt-2 font-mono" placeholder="15-digit GSTIN" />
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Registered address</Label>
                    <Textarea data-testid="comm-address" required value={company.company_address} onChange={setField(company, setCompany)("company_address")} className="rounded-lg bg-secondary border-border mt-2" />
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">City</Label>
                      <Input data-testid="comm-city" required value={company.city} onChange={setField(company, setCompany)("city")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">State</Label>
                      <Input data-testid="comm-state" required value={company.state} onChange={setField(company, setCompany)("state")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Pincode</Label>
                      <Input data-testid="comm-pincode" required value={company.pincode} onChange={setField(company, setCompany)("pincode")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
                    </div>
                  </div>

                  <div className="pt-4 mt-2 border-t border-border">
                    <div className="text-xs uppercase tracking-[0.2em] text-primary mb-4">Fleet ops contact</div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Contact person</Label>
                        <Input data-testid="comm-contact-person" required value={company.contact_person} onChange={setField(company, setCompany)("contact_person")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Phone</Label>
                        <Input data-testid="comm-contact-phone" required value={company.contact_phone} onChange={setField(company, setCompany)("contact_phone")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Email</Label>
                        <Input data-testid="comm-contact-email" type="email" required value={company.contact_email} onChange={setField(company, setCompany)("contact_email")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
                      </div>
                    </div>
                  </div>

                  <Button data-testid="comm-submit" type="submit" disabled={submitting} className="w-full rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-12 mt-4">
                    {submitting ? "Activating…" : "Activate commercial account"} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <p className="text-xs text-muted-foreground text-center">Next step: add your fleet vehicles in one shot from the dashboard.</p>
                </form>
              )}
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
